import React, { useState } from "react";
import { Car } from "lucide-react";

const ParkingSlotGrid = ({ slots, handleEditClick, handleDeleteClick }) => {
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSlotModal, setShowSlotModal] = useState(false);

  const handleSlotClick = (slot) => {
    setSelectedSlot(slot);
    setShowSlotModal(true);
  };

  const closeSlotModal = () => {
    setShowSlotModal(false);
    setSelectedSlot(null);
  };

  const slotBaseStyle = {
    width: "140px",
    height: "220px",
    margin: "8px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    position: "relative",
    transition: "all 0.3s ease",
    cursor: "pointer",
    borderRadius: "8px",
    overflow: "hidden",
  };

  const availableSlotStyle = {
    ...slotBaseStyle,
    background: "#ffffff",
    border: "3px solid #dee2e6",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  const occupiedSlotStyle = {
    ...slotBaseStyle,
    background: "#d1d5db",
    border: "3px solid #9ca3af",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  const reservedSlotStyle = {
    ...slotBaseStyle,
    background: "#fcd34d",
    border: "3px solid #fbbf24",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  };

  const getSlotStyles = (slotType) => {
    switch (slotType) {
      case "open":
        return {
          style: availableSlotStyle,
          iconColor: "#6b7280",
          textColor: "#6b7280",
          label: "AVAILABLE",
          numberBg: "#6b7280",
        };
      case "occupied":
        return {
          style: occupiedSlotStyle,
          iconColor: "#374151",
          textColor: "#374151",
          label: "OCCUPIED",
          numberBg: "#6b7280",
        };
      case "reserved":
        return {
          style: reservedSlotStyle,
          iconColor: "#d97706",
          textColor: "#92400e",
          label: "RESERVED",
          numberBg: "#d97706",
        };
      default:
        return {
          style: slotBaseStyle,
          iconColor: "#6c757d",
          textColor: "#6c757d",
          label: "UNKNOWN",
          numberBg: "#6c757d",
        };
    }
  };

  return (
    <>
      <div className="container-fluid mt-4">
        {/* Parking Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(156px, 1fr))",
            gap: "20px",
            justifyItems: "center",
            padding: "30px",
            // background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
            borderRadius: "12px",
            position: "relative",
            minHeight: "400px",
          }}
        >
          {slots.map((slot) => {
            const slotConfig = getSlotStyles(slot.slot_type);

            return (
              <div
                key={slot.slot_id}
                style={slotConfig.style}
                onClick={() => handleSlotClick(slot)}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform =
                    "scale(1.05) translateY(-5px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 25px rgba(0, 0, 0, 0.2)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1) translateY(0)";
                  const originalShadow = slotConfig.style.boxShadow;
                  e.currentTarget.style.boxShadow = originalShadow;
                }}
                title={`Slot #${slot.slot_number} - ${slotConfig.label}`}
              >
                {/* Parking Lines */}
                <div
                  style={{
                    position: "absolute",
                    top: "0",
                    left: "0",
                    right: "0",
                    height: "8px",
                    // backgroundColor: slotConfig.numberBg,
                    borderRadius: "5px 5px 0 0",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "0",
                    left: "0",
                    right: "0",
                    height: "8px",
                    // backgroundColor: slotConfig.numberBg,
                    borderRadius: "0 0 5px 5px",
                  }}
                />

                {/* Slot Number */}
                <div
                  style={{
                    position: "absolute",
                    top: "12px",
                    right: "12px",
                    // backgroundColor: slotConfig.numberBg,
                    // color: "white",
                    // padding: "6px 10px",
                    borderRadius: "20px",
                    fontSize: "12px",
                    fontWeight: "700",
                    minWidth: "35px",
                    textAlign: "center",
                    // boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                  }}
                >
                  <span className="text-primary-600">#{slot.slot_number}</span>
                </div>

                {/* Main Content */}
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    gap: "12px",
                    paddingTop: "20px",
                  }}
                >
                  {/* Vehicle Icon or Text */}
                  {slot.slot_type === "occupied" ? (
                    <Car
                      size={60}
                      color="#374151"
                      strokeWidth={1.5}
                      style={{
                        filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.3))",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: slotConfig.textColor,
                        textAlign: "center",
                        letterSpacing: "2px",
                        textTransform: "uppercase",
                      }}
                    >
                      {slot.slot_type === "open" ? "OPEN" : "RESERVED"}
                    </div>
                  )}
                </div>

                {/* Parking Bay Corners */}
                <div
                  style={{
                    position: "absolute",
                    top: "15px",
                    left: "15px",
                    width: "15px",
                    height: "15px",
                    borderTop: `3px solid ${slotConfig.numberBg}`,
                    borderLeft: `3px solid ${slotConfig.numberBg}`,
                    borderRadius: "3px 0 0 0",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    bottom: "15px",
                    left: "15px",
                    width: "15px",
                    height: "15px",
                    borderBottom: `3px solid ${slotConfig.numberBg}`,
                    borderLeft: `3px solid ${slotConfig.numberBg}`,
                    borderRadius: "0 0 0 3px",
                  }}
                />
              </div>
            );
          })}
        </div>

        {/* Modal */}
        {showSlotModal && selectedSlot && (
          <>
            <div
              className="modal fade show d-block"
              tabIndex="-1"
              role="dialog"
              style={{
                display: showSlotModal || selectedSlot ? "block" : "none",
                backgroundColor:
                  showSlotModal || selectedSlot ? "rgba(0,0,0,0.5)" : "",
              }}
            >
              <div
                className="modal-dialog modal-dialog-centered"
                role="document"
              >
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">
                      Parking Slot #{selectedSlot.slot_number}
                    </h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={closeSlotModal}
                      aria-label="Close"
                    ></button>
                  </div>

                  <div className="modal-body" style={{ padding: "25px" }}>
                    <div style={{ display: "grid", gap: "20px" }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          marginBottom: "10px",
                        }}
                      >
                        <span
                          style={{
                            padding: "10px 20px",
                            borderRadius: "25px",
                            fontSize: "14px",
                            fontWeight: "700",
                            backgroundColor:
                              selectedSlot.slot_type === "open"
                                ? "var(--success-600)"
                                : selectedSlot.slot_type === "occupied"
                                ? "var(--danger-600)"
                                : "var(--warning-600)",
                            color: "white",
                            textTransform: "uppercase",
                            letterSpacing: "1px",
                          }}
                        >
                          {selectedSlot.slot_type === "open"
                            ? "Available"
                            : selectedSlot.slot_type === "occupied"
                            ? "Occupied"
                            : "Reserved"}
                        </span>
                      </div>

                      <div
                        style={{
                          backgroundColor: "var(--neutral-50)",
                          padding: "20px",
                          borderRadius: "10px",
                          border: "1px solid var(--neutral-200)",
                        }}
                      >
                        <div style={{ display: "grid", gap: "15px" }}>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingBottom: "10px",
                              borderBottom: "2px solid var(--neutral-300)",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                color: "var(--text-primary-light)",
                                fontSize: "14px",
                              }}
                            >
                              Branch:
                            </span>
                            <span
                              style={{
                                color: "var(--neutral-800)",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              {selectedSlot.branch_name}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingBottom: "10px",
                              borderBottom: "2px solid var(--neutral-300)",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                color: "var(--text-primary-light)",
                                fontSize: "14px",
                              }}
                            >
                              Client:
                            </span>
                            <span
                              style={{
                                color: "var(--neutral-800)",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              {selectedSlot.client_name}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              paddingBottom: "10px",
                              borderBottom: "2px solid var(--neutral-300)",
                            }}
                          >
                            <span
                              style={{
                                fontWeight: "600",
                                color: "var(--text-primary-light)",
                                fontSize: "14px",
                              }}
                            >
                              Created:
                            </span>
                            <span
                              style={{
                                color: "var(--neutral-800)",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              {new Date(
                                selectedSlot.created_datetime
                              ).toLocaleDateString()}
                            </span>
                          </div>
                          {selectedSlot.updated_datetime && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span
                                style={{
                                  fontWeight: "600",
                                  color: "var(--text-primary-light)",
                                  fontSize: "14px",
                                }}
                              >
                                Updated:
                              </span>
                              <span
                                style={{
                                  color: "var(--neutral-800)",
                                  fontSize: "14px",
                                  fontWeight: "500",
                                }}
                              >
                                {new Date(
                                  selectedSlot.updated_datetime
                                ).toLocaleDateString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div
                    className="modal-footer"
                    style={{
                      borderTop: "1px solid var(--neutral-200)",
                      padding: "20px",
                      backgroundColor: "var(--neutral-50)",
                      borderBottomLeftRadius: "12px",
                      borderBottomRightRadius: "12px",
                    }}
                  >
                    <div
                      style={{ display: "flex", gap: "12px", width: "100%" }}
                    >
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          handleEditClick(selectedSlot);
                          closeSlotModal();
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: "var(--primary-600)",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          padding: "12px 16px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          transition: "all 0.3s ease",
                          fontSize: "14px",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 4px 12px rgba(230, 60, 35, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        Edit Slot
                      </button>
                      <button
                        type="button"
                        className="btn"
                        onClick={() => {
                          handleDeleteClick(
                            selectedSlot.slot_id,
                            selectedSlot.slot_number
                          );
                          closeSlotModal();
                        }}
                        style={{
                          flex: 1,
                          backgroundColor: "var(--danger-600)",
                          color: "#ffffff",
                          border: "none",
                          borderRadius: "8px",
                          padding: "12px 16px",
                          fontWeight: "600",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: "8px",
                          transition: "all 0.3s ease",
                          fontSize: "14px",
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.transform = "translateY(-2px)";
                          e.target.style.boxShadow =
                            "0 4px 12px rgba(220, 38, 38, 0.3)";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.transform = "translateY(0)";
                          e.target.style.boxShadow = "none";
                        }}
                      >
                        Delete Slot
                      </button>
                    </div>
                    <button
                      type="button"
                      className="btn"
                      onClick={closeSlotModal}
                      style={{
                        width: "100%",
                        marginTop: "12px",
                        backgroundColor: "var(--neutral-500)",
                        border: "none",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        fontWeight: "500",
                        color: "white",
                        fontSize: "14px",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="no-modal fade show"></div>
          </>
        )}
      </div>
    </>
  );
};

export default ParkingSlotGrid;
